import React, { useState, forwardRef, useEffect } from "react";
import { IconButton, Paragraph } from "@tvg/design-system";
import { config, useTransition, animated } from "@react-spring/web";
import {
  Container,
  TextContainer,
  IconButtonContainer,
  FilledStarContainer
} from "./styled-components";

interface SubHeaderProps {
  onClickFavorite: () => void;
  isFavorite: boolean;
  isLoading?: boolean;
}

const FilledStar = forwardRef<HTMLDivElement, { size: number }>(
  ({ size = 5 }, ref) => (
    <FilledStarContainer ref={ref}>
      <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
        <path
          d="M7.99998 1.9502L9.96248 5.92667L14.3508 6.56433L11.1754 9.65958L11.925 14.0301L7.99998 11.9666L4.07496 14.0301L4.82457 9.65958L1.64917 6.56433L6.03747 5.92667L7.99998 1.9502Z"
          fill="#FFC642"
          stroke="#FFC642"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>
    </FilledStarContainer>
  )
);

const AnimatedTextContainer = animated(TextContainer);
const AnimatedStar = animated(FilledStar);

const startData = [
  {
    size: 5,
    top: "-12px",
    left: "14px"
  },
  {
    size: 4,
    top: "-3px",
    left: "-2px"
  },
  {
    size: 4,
    top: "0",
    left: "30px"
  },
  {
    size: 4,
    top: "18px",
    left: "-10px"
  },
  {
    size: 4,
    top: "34px",
    left: "3px"
  },
  {
    size: 5,
    top: "35px",
    left: "30px"
  }
];

const FavoriteButton = ({
  onClickFavorite,
  isFavorite,
  isLoading
}: SubHeaderProps) => {
  const [showText, setShowText] = useState(false);
  const [stars, setStars] = useState(startData);

  const textTransition = useTransition(showText, {
    config: showText ? { ...config.stiff } : { duration: 150 },
    from: { opacity: 0, width: "0" },
    enter: { opacity: 1, width: "98px" },
    leave: { opacity: 0, width: "0" }
  });

  const starsTransitions = useTransition(isFavorite ? stars : [], {
    config: isFavorite
      ? { ...config.molasses, duration: 200 }
      : { duration: 150 },
    from: { opacity: 0, scale: 0, top: "14px", left: "14px" },
    enter: ({ left, top }) => ({ left, top, scale: 1, opacity: 1 }),
    leave: ({ left, top }) => ({ left, top, scale: 1, opacity: 0 }),
    onRest: () => {
      setStars([]);
    }
  });

  useEffect(() => {
    if (!isFavorite) {
      setStars(startData);
    }
  }, [isFavorite]);

  return (
    <Container
      data-qa-label="favorite-button-container"
      onMouseEnter={() => setShowText(true)}
      onMouseLeave={() => setShowText(false)}
    >
      {starsTransitions(
        (styles, item) =>
          item && <AnimatedStar style={styles} size={item.size} />
      )}
      <IconButtonContainer isFavorite={isFavorite}>
        <IconButton
          variant="tertiary_dark"
          qaLabel={`favorite-button-${
            isFavorite ? "selected" : "not-selected"
          }`}
          size="s"
          iconName="star"
          onPress={() => {
            onClickFavorite();
          }}
          isDisabled={isLoading}
        />
      </IconButtonContainer>
      {textTransition(
        (styles, item) =>
          item && (
            <AnimatedTextContainer
              style={styles}
              onClick={!isLoading && onClickFavorite}
            >
              <Paragraph
                qaLabel="favorite-button-text"
                fontSize="xs"
                color="white.900"
                fontFamily="medium"
              >
                {isFavorite ? "Remove Favorite" : "Add as Favorite"}
              </Paragraph>
            </AnimatedTextContainer>
          )
      )}
    </Container>
  );
};

export default FavoriteButton;
